<template>
  <div class="flex">
    <div class="w-full h-full p-2 relative" ref="container">
      <q-table
        ref="table"
        v-if="height"
        class="my-sticky-virtscroll-table w-full font-semibold h-full"
        :style="{ maxHeight: `${height}px`, maxWidth: `${width}px` }"
        virtual-scroll
        :rows-per-page-options="[0]"
        :virtual-scroll-sticky-size-start="48"
        :virtual-scroll-slice-size="scrollSize"
        row-key="assetName"
        :pagination="{
          rowsPerPage: 0,
        }"
        :data="data"
        :columns="setColumns"
        :visible-columns="visibleColumns"
        hide-bottom
      >
        <template #header="props">
          <q-tr :props="props">
            <q-th
              v-for="col in props.cols"
              :key="col.name"
              :props="props"
              class="font-bold"
              style="z-index: 999"
            >
              {{ col.label }}
            </q-th>
          </q-tr>
        </template>
        <template #body="props">
          <q-tr
            :props="props"
            @click="$emit('selected', props.row)"
            :class="{ 'bg-blue-1': props.row.assetId == selectedUnit }"
          >
            <q-td key="assetName" :props="props" style="padding: 0">
              <div class="fit flex flex-no-wrap">
                <div
                  class="w-2"
                  :class="`bg-${statusList[props.row.status]}`"
                />
                <div
                  class="flex-1 flex flex-no-wrap items-center justify-between px-4 text-sm font-bold"
                >
                  <span
                    class="py-2 flex items-center"
                    style="
                      overflow: clip;
                      text-overflow: ellipsis;
                      display: -webkit-box;
                      -webkit-line-clamp: 2;
                      -webkit-box-orient: vertical;
                      height: 100%;
                      max-height: 50px;
                      word-wrap: break-word;
                      white-space: normal;
                      min-width: 200px;
                    "
                  >
                    <span class="flex w-full justify-between">
                      <span>
                        {{ props.row.assetName }}
                      </span>
                      <span
                        class="pl-2"
                        v-if="
                          nearbyClustered[props.row.assetId] &&
                          nearbyClustered[props.row.assetId].distance
                        "
                      >
                        -
                        {{ nearbyClustered[props.row.assetId].distance }}
                        KM</span
                      >
                    </span>
                  </span>
                  <div
                    class="cursor-pointer w-6 h-full flex flex-center"
                    @mouseover="menuOver = { [props.row.assetId]: true }"
                    @mouseout="menuOver = false"
                  >
                    <q-icon
                      name="expand_more"
                      class="cursor-pointer w-6 h-full flex flex-center pointer-events-none"
                    >
                      <q-menu
                        v-model="menu[props.row.assetId]"
                        persistent
                        auto-close
                      >
                        <q-list
                          style="min-width: 100px"
                          dense
                          @mouseover.native="
                            listOver = { [props.row.assetId]: true }
                          "
                          @mouseout.native="listOver = false"
                        >
                          <q-item
                            clickable
                            v-close-popup
                            v-for="(n, i) in [
                              { l: 'Mapview', a: 'map' },
                              { l: 'Command', a: 'map-single' },
                              { l: 'Playback', a: 'playback' },
                              { l: 'Reports', a: 'reports' },
                              { l: 'Share Live Tracking', a: 'share-location' },
                            ]"
                            :key="n.l"
                          >
                            <q-item-section>
                              <router-link
                                @click.native="
                                  i == 4
                                    ? $emit('share-location', props.row)
                                    : null
                                "
                                :to="
                                  i == 4
                                    ? `${$route.fullPath}`
                                    : i == 0
                                      ? {
                                          name:
                                            $route.name == 'monitor'
                                              ? 'monitor'
                                              : n.a,
                                          params: {
                                            view:
                                              $route.name == 'monitor'
                                                ? 'map'
                                                : void 0,
                                            assetId: props.row.assetId,
                                            assetName: props.row.assetName,
                                          },
                                        }
                                      : {
                                          name: n.a,
                                          params: {
                                            assetId: props.row.assetId,
                                            assetName: props.row.assetName,
                                          },
                                        }
                                "
                                >{{ n.l }}</router-link
                              ></q-item-section
                            >
                          </q-item>
                        </q-list>
                      </q-menu>
                    </q-icon>
                  </div>
                </div>
              </div>
            </q-td>
            <q-td key="lastUpdated" :props="props" class="font-medium text-sm">
              <div
                class="flex flex-col items-start break-words whitespace-normal gap-1"
                style="max-width: 8vw"
              >
                <span class="flex flex-no-wrap gap-2 text-xs font-bold">
                  <q-badge color="white" style="border-radius: 50px">
                    <q-icon name="calendar_today" color="purple" size="8px" />
                  </q-badge>
                  {{ props.row.lastUpdated.split(' ')[0] }}
                </span>
                <span class="flex flex-no-wrap gap-2 text-xs">
                  <q-badge color="white" style="border-radius: 50px">
                    <q-icon name="schedule" color="teal" size="9px" />
                  </q-badge>
                  {{ props.row.lastUpdated.split(' ').at(-1) }}
                </span>
              </div>
            </q-td>
            <q-td key="Ignition" :props="props" class="font-bold text-xs">
              <q-badge
                class="font-bold"
                :color="statusList[props.row.status]"
                style="font-size: 10px; white-space: normal"
              >
                {{ props.row.ignition }}
              </q-badge>
            </q-td>
            <q-td key="Volt" :props="props" class="font-bold text-xs">
              {{ props.row.volt }}
            </q-td>
            <q-td
              key="Batt"
              :props="props"
              class="font-bold text-xs"
              style="max-width: 60px"
            >
              {{ props.row.batt }}%
            </q-td>
            <q-td
              key="idlingStatus"
              :props="props"
              class="font-bold text-xs"
              style="width: 60px"
            >
              <span v-if="props.row.idlingStatus != '-'">
                {{ props.row.idlingStatus }}
              </span>
            </q-td>
            <q-td
              key="Speed"
              :props="props"
              class="font-bold text-xs"
              style="max-width: 60px"
            >
              {{ props.row.speed }}
            </q-td>
            <q-td key="BP" :props="props" class="" style="width: 80px">
              <span v-if="props.row.watchMeta == '-'">-</span>
              <span v-else
                >{{ props.row.watchMeta.highPressure }}/{{
                  props.row.watchMeta.lowPressure
                }}
                mmHg</span
              >
            </q-td>
            <q-td key="HR" :props="props" class="" style="width: 80px">
              <span v-if="props.row.watchMeta == '-'">-</span>
              <span v-else>{{ props.row.watchMeta.heartRate }}</span>
            </q-td>
            <q-td key="SPO2" :props="props" class="" style="width: 80px">
              <span v-if="props.row.watchMeta == '-'">-</span>
              <span v-else>{{ props.row.watchMeta.spO2 }}%</span>
            </q-td>
            <q-td key="Steps" :props="props" class="" style="width: 80px">
              <span v-if="props.row.watchMeta == '-'">-</span>
              <span v-else>{{ props.row.watchMeta.steps }}</span>
            </q-td>
            <q-td
              key="mileage"
              :props="props"
              class="font-bold text-xs"
              style="width: 100px"
            >
              {{ props.row.mileage }}
            </q-td>
            <q-td key="Fuel" :props="props" class="text-xs">
              <div class="flex flex-center">
                <q-card
                  flat
                  v-if="props.row.fuel != '-'"
                  class="flex flex-no-wrap flex-center px-1 py-2 cursor-pointer bg-teal-2 text-cyan-10 font-bold"
                  :class="
                    getFuel(props.row.fuel).length > 1
                      ? 'flex-col gap-0'
                      : 'gap-1'
                  "
                  style="width: 60px"
                  @click="$emit('chart', ['fuel', props.row])"
                >
                  <span
                    style="font-size: 8px"
                    v-for="(n, i) in getFuel(props.row.fuel)"
                    :key="i"
                    class="flex flex-no-wrap relative"
                    :class="{
                      'w-full justify-between':
                        getFuel(props.row.fuel).length > 1,
                    }"
                  >
                    <span v-if="getFuel(props.row.fuel).length == 1">
                      {{ n[0] }}
                    </span>
                    <span
                      v-else
                      v-for="(t, i2) in n"
                      :key="t"
                      :class="[
                        i2 == 1
                          ? `bg-${getStatusFromPercentage(t)} text-black`
                          : '',
                      ]"
                      :style="{ width: `${i2 == 1 ? '30px' : void 0}` }"
                    >
                      <span>
                        {{ t }}
                      </span>
                      <span v-if="i2 == 1"> % </span>
                    </span>
                    <span
                      class="bg-grey-7 w-full absolute bottom-0 left-0"
                      style="height: 1px"
                      v-if="i != getFuel(props.row.fuel).length - 1"
                    />
                  </span>

                  <q-knob
                    v-if="getFuel(props.row.fuel).length == 1"
                    readonly
                    show-value
                    font-size="8px"
                    :value="getFuel(props.row.fuel)[0][1]"
                    size="25px"
                    :thickness="0.22"
                    :color="
                      getStatusFromPercentage(getFuel(props.row.fuel)[0][1])
                    "
                    track-color="grey-3"
                  >
                    {{ getFuel(props.row.fuel)[0][1] }}%
                  </q-knob>
                  <q-tooltip content-class="bg-primary">
                    View Daily Fuel Chart
                  </q-tooltip>
                  <q-inner-loading
                    :showing="isLoading(chart, 'fuel', loading, props.rowIndex)"
                  >
                    <q-spinner size="20px" color="primary" />
                  </q-inner-loading>
                </q-card>
              </div>
            </q-td>
            <q-td
              key="Temp"
              :props="props"
              class="font-bold text-xs"
              style="width: 80px"
            >
              <q-card
                flat
                v-if="props.row.temp != '-'"
                class="flex justify-between items-center flex-no-wrap gap-1 px-2 py-2 cursor-pointer bg-teal-2 text-cyan-10 font-bold"
                style="width: 60px"
                @click="$emit('chart', ['temp', props.row])"
              >
                <q-icon name="thermostat" color="danger" />
                <div class="flex flex-col" style="font-size: 8px">
                  <span v-for="(n, i) in props.row.temp.split(',')" :key="i">
                    {{ n }}
                  </span>
                </div>

                <q-tooltip content-class="bg-primary">
                  View Daily Temperature Chart
                </q-tooltip>
                <q-inner-loading
                  :showing="isLoading(chart, 'temp', loading, props.rowIndex)"
                >
                  <q-spinner size="20px" color="primary" />
                </q-inner-loading>
              </q-card>
            </q-td>
            <q-td
              key="Humidity"
              :props="props"
              class="font-bold text-xs"
              style="width: 50px"
            >
              <q-card
                flat
                v-if="props.row.humidity != '-'"
                class="flex flex-center flex-no-wrap gap-1 px-2 py-2 bg-teal-2 text-cyan-10 font-bold"
                style="width: 50px"
              >
                <div class="flex flex-col" style="font-size: 8px">
                  <span
                    v-for="(n, i) in props.row.humidity.split(',')"
                    :key="i"
                  >
                    {{ n }}
                  </span>
                </div>
              </q-card>
            </q-td>
            <q-td key="gps" :props="props" class="" style="width: 80px">
              <q-badge
                :color="`${props.row.gps == 'YES' ? 'cyan-1' : 'pink-1'}`"
                :text-color="`${props.row.gps == 'YES' ? 'success' : 'red-10'}`"
                class="flex flex-center font-bold text-xs"
              >
                {{ props.row.gps }}
              </q-badge>
            </q-td>

            <q-td key="input" :props="props" class="font-bold text-xs">
              <div class="row">
                <q-card
                  flat
                  v-if="props.row.input != '-'"
                  class="flex flex-center px-3 py-2 bg-teal-2 text-cyan-10 font-bold text-center"
                  :class="{
                    inputOn: props.row.input.includes('ON') === true,
                  }"
                  style="
                    width: 40px;
                    margin-right: 5px;
                    font-size: 8px;
                    white-space: normal;
                    filter: hue-rotate();
                  "
                >
                  {{ props.row.input }}
                </q-card>
                <q-card
                  flat
                  v-if="props.row.input2 != '-'"
                  class="flex flex-center px-3 py-2 bg-teal-2 text-cyan-10 font-bold text-center"
                  :class="{
                    inputOn: props.row.input2.includes('ON') === true,
                  }"
                  style="
                    width: 40px;
                    margin-right: 5px;
                    font-size: 8px;
                    white-space: normal;
                    filter: hue-rotate();
                  "
                >
                  {{ props.row.input2 }}
                </q-card>
                <q-card
                  flat
                  v-if="props.row.input3 != '-'"
                  class="flex flex-center px-3 py-2 bg-teal-2 text-cyan-10 font-bold text-center"
                  :class="{
                    inputOn: props.row.input3.includes('ON') === true,
                  }"
                  style="
                    width: 40px;
                    font-size: 8px;
                    white-space: normal;
                    filter: hue-rotate();
                  "
                >
                  {{ props.row.input3 }}
                </q-card>
              </div>
            </q-td>

            <q-td key="output" :props="props" class="font-bold text-xs">
              <div class="row">
                <q-card
                  flat
                  v-if="props.row.output != '-'"
                  class="flex flex-center px-3 py-2 bg-teal-2 text-cyan-10 font-bold text-center"
                  :class="{
                    inputOn: props.row.output.includes('ON') === true,
                  }"
                  style="
                    width: 40px;
                    margin-right: 5px;
                    font-size: 8px;
                    white-space: normal;
                    filter: hue-rotate();
                  "
                >
                  {{ props.row.output }}
                </q-card>
                <q-card
                  flat
                  v-if="props.row.output2 != '-'"
                  class="flex flex-center px-3 py-2 bg-teal-2 text-cyan-10 font-bold text-center"
                  :class="{
                    inputOn: props.row.output2.includes('ON') === true,
                  }"
                  style="
                    width: 40px;
                    font-size: 8px;
                    white-space: normal;
                    filter: hue-rotate();
                  "
                >
                  {{ props.row.output2 }}
                </q-card>
              </div>
            </q-td>
            <q-td key="video" :props="props" class="" style="width: 80px">
              <q-card
                v-if="props.row.video != '-'"
                flat
                class="flex justify-between items-center flex-no-wrap gap-1 px-2 py-2 cursor-pointer bg-teal-2 text-cyan-10 font-bold"
                style="width: 30px"
                @click="setVideo(props.row)"
              >
                <q-icon name="videocam" />
                <q-tooltip content-class="bg-primary">
                  View Live Stream {{ props.row.video }}
                </q-tooltip>
                <q-dialog
                  v-model="videoDialog"
                  :maximized="true"
                  transition-show="slide-up"
                  transition-hide="slide-down"
                  @escape-key="videoDialog = false"
                >
                  <q-card class="bg-primary text-white flex flex-col">
                    <q-bar>
                      <q-space />

                      <q-btn dense flat icon="close" v-close-popup>
                        <q-tooltip class="bg-white text-primary"
                          >Close</q-tooltip
                        >
                      </q-btn>
                    </q-bar>

                    <q-card-section class="flex-1 p-0">
                      <iframe
                        class="h-full w-full"
                        :src="`https://hardware.igeotechgps.com/?terminalId=${props.row.unitId}`"
                        frameborder="0"
                        scrolling="no"
                        marginheight="0"
                        marginwidth="0"
                      ></iframe>
                    </q-card-section>
                  </q-card>
                </q-dialog>
              </q-card>
              <div v-else>{{ props.row.video }}</div>
            </q-td>
            <q-td key="address" :props="props" class="font-bold text-xs">
              <div
                style="
                  overflow: clip;
                  text-overflow: ellipsis;
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  height: 31px;
                  word-wrap: break-word;
                  white-space: normal;
                  max-width: 300px;
                  /* width: 250px; */
                "
                class="address-hover cursor-pointer"
                @click="$emit('map', props.row)"
              >
                {{ props.row.address }}
              </div>
              <q-tooltip content-class="bg-black font-bold" max-width="300px">
                {{ props.row.address }}
              </q-tooltip>
            </q-td>
            <q-td key="Beacon" :props="props" class="" style="width: 80px">
              <span v-if="props.row.watchMeta == '-'">-</span>
              <span v-else>{{ props.row.watchMeta.beacon }}</span>
            </q-td>
            <q-td key="Status" :props="props" class="font-bold text-xs p-0">
              <!-- <div
                style="width: 100px"
                class="fit flex flex-center text-white"
                :class="`bg-${statusList[props.row.status]}`"
              >
                {{ props.row.status }}
              </div> -->

              <!-- <div
                style="width: 100px"
                class="fit flex flex-center"
                :class="`text-${statusList[props.row.status]}`"
              >
                {{ props.row.status }}
              </div> -->
              <div class="fit flex flex-center">
                <q-badge
                  class="flex flex-center font-bold"
                  :color="statusList[props.row.status]"
                  style="width: 60px"
                >
                  {{ props.row.status }}
                </q-badge>
              </div>
            </q-td>
          </q-tr>
        </template>
        <template #pagination="scope">
          <div class="mr-4">
            <!-- {{ inViewRange }} -->
            {{ data.length }}
          </div>
        </template>
      </q-table>

      <div
        v-if="firstLoad"
        class="absolute h-full w-full left-0 top-0 p-4 font-bold text-danger"
      >
        <q-inner-loading showing color="primary" />
      </div>
      <div
        v-if="customError && data.length < 1"
        class="fixed p-4 font-bold text-danger"
        :style="{
          height: `${height}px`,
          width: `${width}px`,
          left: `${customError.left}px`,
          top: `${customError.top}px`,
        }"
      >
        <q-icon name="warning" />
        No Data
      </div>
    </div>
    <Transition name="scale">
      <share-location
        v-if="showLiveLocation == true"
        @close-modal="showLiveLocation = false"
        :data="liveLocationPopupData"
      />
    </Transition>
  </div>
</template>
<script setup>
import { useElementSize, useStorage } from '@vueuse/core';
import ShareLocation from './ShareLocation.vue';
import { debounce } from 'quasar';
// import { customCss } from './quasar.js';
import { watch, onUnmounted, ref, computed, defineProps, onMounted } from 'vue';
import { findNearbyState } from '../hybrid-view/findNearby';

const props = defineProps({
  data: {},
  chart: {},
  loading: {},
  visibleColumns: {},
  scrollSizeItem: { default: 90 },
  selectedUnit: { default: '' },
});

const table = ref();

const videoDialog = ref(false);
function setVideo(row) {
  videoDialog.value = true;
}

const menu = ref({});
const menuOver = ref({});
const listOver = ref({});
function checkMenu() {
  menu.value = menuOver.value || listOver.value;
}

const userInfo = JSON.parse(localStorage.getItem('userInfo'));

const debounceMenu = debounce(checkMenu, 300);
watch(menuOver, (v) => {
  if (v) {
    menu.value = v;
  } else {
    debounceMenu();
  }
});
watch(listOver, (v) => {
  if (v) {
    menu.value = v;
  } else {
    debounceMenu();
  }
});

watch(
  () => props.selectedUnit,
  () => {
    retryScrollTo();
  },
  { immediate: true },
);
function retryScrollTo() {
  if (table.value && table.value.computedRows) {
    scrollToSelected();
  } else {
    setTimeout(() => {
      retryScrollTo();
    }, 200);
  }
}
function scrollToSelected() {
  let index = getIndexByAssetId(table.value.computedRows, props.selectedUnit);
  if (index) {
    table.value.scrollTo(index - 1, 'start');
  }
}
function getIndexByAssetId(array, assetId) {
  for (let i = 0; i < array.length; i++) {
    if (array[i].assetId === assetId) {
      return i + 1; // Return the index if found
    }
  }
  return false; // Return -1 if not found
}

function isLoading(c1, c2, i1, i2) {
  if (c1 == c2 && i1 == i2) {
    return true;
  }
}

const statusList = {
  Moving: 'success',
  Idling: 'warning',
  Parked: 'danger',
  Inactive: 'grey-8',
};

// const { id, css, load, unload } = useStyleTag(customCss, { id: 'naruto' });

const scrollSize = ref(5);
const customError = ref(null);

const loaddedData = ref(0);
const firstLoad = ref(true);
onMounted(() => {
  if (props.data.length > 0) {
    firstLoad.value = false;
  }
});
watch(
  () => props.data,
  (v) => {
    // console.log(props.data);
    if (v.length > 0) {
      firstLoad.value = true;
    }
    if (loaddedData.value) {
      if (v && v.length) {
        firstLoad.value = false;
      }
    }
    if (!loaddedData.value) {
      loaddedData.value++;
      if (!v || !v.length) {
        setTimeout(() => {
          firstLoad.value = false;
        }, 8000);
      }
    }
    let body = document.querySelector('.q-virtual-scroll__padding');
    if (body) {
      customError.value = body.getBoundingClientRect();
    }
    setTimeout(() => {
      scrollSize.value = props.scrollSizeItem;
    }, 2500);
  },
  { immediate: true },
);

const columns = ref([
  {
    name: 'assetName',
    label: 'Asset',
    // style: 'width:25%',
  },
  {
    name: 'lastUpdated',
    label: 'Time',
    // style: 'width:5%',
  },
  {
    name: 'Ignition',
    // style: 'width:5%',
  },
  {
    name: 'Volt',
    // style: 'width:5%',
  },
  {
    name: 'idlingStatus',
    label: 'Idling',
    // style: 'width:5%',
  },
  {
    name: 'Speed',
    // style: 'width:5%',
  },
  {
    name: 'mileage',
    label: 'Odometer',
    // style: 'width:5%',
  },
  {
    name: 'Fuel',
    align: 'center',
  },
  {
    name: 'Temp',
  },
  {
    name: 'Humidity',
  },
  {
    name: 'gps',
    label: 'GPS',
  },
  {
    name: 'input',
    label: 'Input',
  },
  {
    name: 'output',
    label: 'Output',
  },
  {
    name: 'video',
    label: 'Video',
  },
  {
    name: 'address',
    label: 'Location',
  },
  {
    name: 'Status',
    align: 'center',
  },
]);

const container = ref();
const { width, height } = useElementSize(container);
// onMounted(async () => {
//   await nextTick();
//   let bounding = container.value.getBoundingClientRect();
//   height.value = `${bounding.height}px`;
//   width.value = `${bounding.width}px`;
// });
const setColumns = computed(() => {
  if (
    userInfo.username == 'mhbadmin' ||
    userInfo.username == 'ironwatch' ||
    userInfo.username == 'demowatch'
  ) {
    const arr = [
      {
        align: 'left',
        field: 'assetName',
        sortable: true,
        name: 'assetName',
        label: 'Asset',
      },
      {
        align: 'left',
        field: 'lastUpdated',
        sortable: true,
        name: 'lastUpdated',
        label: 'Time',
      },
      { align: 'left', field: 'ignition', label: 'Power', name: 'Ignition' },
      { align: 'left', field: 'Batt', label: 'Batt', name: 'Batt' },
      {
        align: 'left',
        field: 'idlingStatus',
        name: 'idlingStatus',
        label: 'Idling',
      },
      { align: 'left', field: 'speed', label: 'Speed', name: 'Speed' },
      { align: 'left', field: 'BP', name: 'BP', label: 'BP' },
      { align: 'left', field: 'HR', name: 'HR', label: 'HR' },
      { align: 'left', field: 'SPO2', name: 'SPO2', label: 'SPO2' },
      { align: 'left', field: 'Steps', name: 'Steps', label: 'Steps' },
      { align: 'left', field: 'gps', name: 'gps', label: 'GPS' },
      { align: 'left', field: 'address', name: 'address', label: 'Location' },
      { align: 'left', field: 'Beacon', name: 'Beacon', label: 'Beacon' },
      {
        align: 'center',
        field: 'status',
        sortable: true,
        label: 'Status',
        name: 'Status',
      },
    ];

    return arr;
  } else {
    let arr = [];
    columns.value.forEach((e) => {
      let obj = {
        align: 'left',
        field: e.name,
        sortable: true,
      };
      if (!e.label) {
        obj.label = e.name;
        obj.field = e.name.toLowerCase();
      }
      arr.push({ ...obj, ...e });
    });
    return arr;
  }
});

function getFuel(string) {
  let arr = string.split('\n');
  let result = [];
  if (arr.length > 1) {
    let liter = arr[0].split(',');
    let val = getStringBetweenParentheses(arr[1]);

    liter.forEach((e, i) => {
      result.push([e.trim(), val[i]]);
    });
  } else {
    let v = arr[0].split(' ');
    result.push([v[0].trim(), getStringBetweenParentheses(v[1])[0]]);
  }
  return result;
}

function getStringBetweenParentheses(inputString) {
  // Use regular expression to extract numeric values
  const numericValues = inputString.match(/\d+/g);

  // Convert the extracted values to integers
  return numericValues.map((value) => parseInt(value, 10));
}
function getStatusFromPercentage(percentage) {
  if (percentage >= 0 && percentage <= 30) {
    return 'danger';
  } else if (percentage > 30 && percentage <= 70) {
    return 'warning';
  } else if (percentage > 70 && percentage <= 100) {
    return 'success';
  } else {
    return 'Invalid percentage';
  }
}

const inView = ref([]);
const inViewRange = computed(() => {
  return `${Number(inView.value[0]) + 1} - ${
    Number(inView.value.at(-1)) + 1
  } of`;
});
function onIntersection(entry) {
  if (entry.isIntersecting === true) {
    add(entry.target.dataset.id);
  } else {
    remove(entry.target.dataset.id);
  }
}
function add(i) {
  remove(i);
  inView.value.push(i);
  inView.value.sort(sortAtoi);
}

function remove(i) {
  let index;
  while ((index = inView.value.indexOf(i)) > -1) {
    inView.value.splice(index, 1);
    inView.value.sort(sortAtoi);
  }
}

function sortAtoi(a, b) {
  return Number(a) - Number(b);
}

const showLiveLocation = ref(false);
const liveLocationPopupData = ref();

function shareLiveLocation(rowData) {
  liveLocationPopupData.value = rowData;
  showLiveLocation.value = true;
}

const nearbyClustered = computed(() => {
  if (findNearbyState.value.length == 0) return {};
  let obj = {};
  findNearbyState.value.forEach((e) => {
    obj[e.assetId] = e;
  });
  return obj;
});
</script>

<script>
export default {
  methods: {
    openPopup(v) {
      this.$refs[`popup${v}`].show();
    },
    closePopup(v) {
      this.$refs[`popup${v}`].hide();
    },
  },
};
</script>

<style lang="sass">
.my-sticky-virtscroll-table
  /* height or max-height is important */

  .q-table__top,
  .q-table__bottom,
  thead tr:first-child th /* bg color is important for th; just specify one */
    background-color: #fff

  thead tr th
    position: sticky
    z-index: 1
  /* this will be the loading indicator */
  thead tr:last-child th
    /* height of all previous header rows */
    top: 48px
  thead tr:first-child th
    top: 0

  /* prevent scrolling behind sticky top row on focus */
  tbody
    /* height of all previous header rows */
    scroll-margin-top: 48px
</style>
<style scoped>
@-webkit-keyframes inputOn {
  from {
    /* filter: invert(0); */
    filter: hue-rotate(0deg) saturate(1);
  }
  to {
    /* filter: invert(1); */

    filter: hue-rotate(-90deg) saturate(2);
  }
}
@-moz-keyframes inputOn {
  from {
    /* filter: invert(0); */
    filter: hue-rotate(0deg) saturate(1);
  }
  to {
    /* filter: invert(1); */

    filter: hue-rotate(-90deg) saturate(2);
  }
}
@-o-keyframes inputOn {
  from {
    /* filter: invert(0); */
    filter: hue-rotate(0deg) saturate(1);
  }
  to {
    /* filter: invert(1); */

    filter: hue-rotate(-90deg) saturate(2);
  }
}
@keyframes inputOn {
  from {
    /* filter: invert(0); */
    filter: hue-rotate(0deg) saturate(1);
  }
  to {
    /* filter: invert(1); */

    filter: hue-rotate(-90deg) saturate(2);
  }
}
.inputOn {
  -webkit-animation: inputOn 0.8s infinite alternate-reverse; /* Safari 4+ */
  -moz-animation: inputOn 0.8s infinite alternate-reverse; /* Fx 5+ */
  -o-animation: inputOn 0.8s infinite alternate-reverse; /* Opera 12+ */
  animation: inputOn 0.8s infinite alternate-reverse; /* IE 10+ */
}
.address-hover:hover {
  color: rgb(39, 125, 255);
  transition: 0.3s;
}
</style>
